import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import "@/App.css";

import AppLayout from "@/components/layout/AppLayout";
import Login from "@/pages/Login";
import ChangePassword from "@/pages/ChangePassword";
// R-017 — `Dashboard` is unmounted until the dashboard/command_center module is
// ported; re-add the import with the RoleHome branch below.
// import Dashboard from "@/pages/Dashboard";
import Users from "@/pages/Users";
import Patients from "@/pages/Patients";
import Facilities from "@/pages/Facilities";
import Roles from "@/pages/Roles";
import Lookups from "@/pages/Lookups";
import Audit from "@/pages/Audit";
import Protocols from "@/pages/Protocols";
import Reports from "@/pages/Reports";
import SmsConsole from "@/pages/SmsConsole";
import CareTeams from "@/pages/CareTeams";

import OnboardingDashboard from "@/pages/onboarding/OnboardingDashboard";
import RequestList from "@/pages/onboarding/RequestList";
import CreateRequest from "@/pages/onboarding/CreateRequest";
import RequestDetail from "@/pages/onboarding/RequestDetail";

import HotlineDashboard from "@/pages/hotline/HotlineDashboard";
import HotlineSessions from "@/pages/hotline/HotlineSessions";
import NewHotlineSession from "@/pages/hotline/NewHotlineSession";
import HotlineSessionDetail from "@/pages/hotline/HotlineSessionDetail";

import RpmDashboard from "@/pages/rpm/RpmDashboard";
import RpmPatientDetail from "@/pages/rpm/RpmPatientDetail";
import RpmAlerts from "@/pages/rpm/RpmAlerts";
import RpmInterventions from "@/pages/rpm/RpmInterventions";
import RpmThresholds from "@/pages/rpm/RpmThresholds";

import FleetDashboard from "@/pages/fleet/FleetDashboard";
import FleetMap from "@/pages/fleet/FleetMap";
import FleetVisits from "@/pages/fleet/FleetVisits";
import FleetClinicians from "@/pages/fleet/FleetClinicians";
import FleetVehicles from "@/pages/fleet/FleetVehicles";

import { TeleconsultDashboard, TeleconsultRoom } from "@/pages/Teleconsult";

import ClinicalDashboard from "@/pages/clinical/ClinicalDashboard";
import ClinicalTasks from "@/pages/clinical/ClinicalTasks";
import ClinicalPatientProfile from "@/pages/clinical/ClinicalPatientProfile";

import {
  ExecutiveDashboard, OperationalDashboard, ClinicalSafetyDashboard,
  PopulationHealthDashboard, InterventionsDashboard, SlaManager,
  PatientExplorer,
} from "@/pages/command/CommandCenter";

/*
 * R-017 — where to send someone when their natural landing page has no backendv2
 * module yet.
 *
 * Every pre-port landing (`/` Dashboard, `/hotline`, `/clinical`, `/onboarding`)
 * belongs to a module that is still spec-only, so each one drops the user on a
 * dead screen. Resolve the first destination they can actually reach instead,
 * in descending usefulness.
 *
 * `adminOnly` mirrors the route table below — the API opens patient/facility/
 * lookup reads to every authenticated role, but the SPA has not yet split those
 * screens, so a non-admin cannot land on them.
 *
 * Delete an entry from here when its module lands and the natural landing works
 * again.
 */
const PORT_LANDINGS = [
  { to: "/patients", permission: "patients.view", adminOnly: true },
  { to: "/users", permission: "users.view", adminOnly: true },
  { to: "/teleconsult", permission: "teleconsult.view", adminOnly: false },
  { to: "/facilities", permission: "facilities.view", adminOnly: true },
];

function resolveLanding(user, can) {
  const isAdmin = user?.role_code === "admin";
  // Fail open if the API didn't serialize permissions — same rationale as the
  // sidebar's permissionFilter.
  const hasPerms = Array.isArray(user?.permissions);
  const hit = PORT_LANDINGS.find(
    (l) => (isAdmin || !l.adminOnly) && (!hasPerms || can(l.permission))
  );
  return hit?.to ?? null;
}

/* Nothing ported is reachable for this user — say so instead of looping. */
function NoLanding() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center px-6">
      <div className="max-w-md text-center">
        <div className="text-[#1F2937] dark:text-[#E5E7EB] text-lg font-semibold">
          Nothing available yet
        </div>
        <p className="mt-2 text-sm text-[#6B7280]">
          The modules for your role are still being migrated to the new backend.
          Anything marked “Soon” in the sidebar is not live yet.
        </p>
      </div>
    </div>
  );
}

function Protected({ children, adminOnly }) {
  const { user, loading, can } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#6B7280] text-sm" data-testid="auth-loading">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // Forced first-login reset (R-008): block the whole app until the password is
  // changed. Rendered full-screen here so no layout/PHI leaks behind it.
  if (user.must_change_password) return <ChangePassword />;
  if (adminOnly && user.role_code !== "admin") {
    // R-017 — was `/onboarding`, an unported module, so a non-admin hitting an
    // admin route landed on a dead page.
    const landing = resolveLanding(user, can);
    return landing ? <Navigate to={landing} replace /> : <NoLanding />;
  }
  return children;
}

function RoleHome() {
  const { user, can } = useAuth();
  if (!user) return null;

  /*
   * R-017 — every one of the original landings (`<Dashboard />`, `/hotline`,
   * `/clinical`, `/onboarding`) is spec-only on backendv2, so the role-based
   * routing below is disabled until those modules land. Restore a branch as its
   * module ships; the resolver is the interim fallback for everyone.
   *
   *   if (user.role_code === "admin") return <Dashboard />;
   *   if (user.role_code === "hah_nurse") return <Navigate to="/hotline" replace />;
   *   if (["physician", "hah_physician", "nurse", "care_coordinator",
   *        "hah_coordinator", "clinical_director"].includes(user.role_code))
   *     return <Navigate to="/clinical" replace />;
   *   return <Navigate to="/onboarding" replace />;
   */
  const landing = resolveLanding(user, can);
  return landing ? <Navigate to={landing} replace /> : <NoLanding />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster richColors position="top-right" />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/change-password" element={<Protected><ChangePassword /></Protected>} />
          <Route
            path="/"
            element={
              <Protected>
                <AppLayout />
              </Protected>
            }
          >
            <Route index element={<RoleHome />} />
            <Route path="users" element={<Protected adminOnly><Users /></Protected>} />
            <Route path="patients" element={<Protected adminOnly><Patients /></Protected>} />
            <Route path="facilities" element={<Protected adminOnly><Facilities /></Protected>} />
            <Route path="roles" element={<Protected adminOnly><Roles /></Protected>} />
            <Route path="lookups" element={<Protected adminOnly><Lookups /></Protected>} />
            <Route path="audit" element={<Protected adminOnly><Audit /></Protected>} />
            <Route path="protocols" element={<Protected adminOnly><Protocols /></Protected>} />
            <Route path="reports" element={<Reports />} />
            <Route path="sms" element={<Protected adminOnly><SmsConsole /></Protected>} />
            <Route path="care-teams" element={<CareTeams />} />

            <Route path="onboarding" element={<OnboardingDashboard />} />
            <Route path="onboarding/new" element={<CreateRequest mode="new" />} />
            <Route path="onboarding/requests" element={<RequestList />} />
            <Route path="onboarding/requests/:id" element={<RequestDetail />} />
            <Route path="onboarding/requests/:id/edit" element={<CreateRequest mode="edit" />} />

            <Route path="hotline" element={<HotlineDashboard />} />
            <Route path="hotline/new" element={<NewHotlineSession />} />
            <Route path="hotline/sessions" element={<HotlineSessions />} />
            <Route path="hotline/sessions/:id" element={<HotlineSessionDetail />} />

            <Route path="rpm" element={<RpmDashboard />} />
            <Route path="rpm/patients/:id" element={<RpmPatientDetail />} />
            <Route path="rpm/alerts" element={<RpmAlerts />} />
            <Route path="rpm/interventions" element={<RpmInterventions />} />
            <Route path="rpm/thresholds" element={<Protected adminOnly><RpmThresholds /></Protected>} />

            <Route path="fleet" element={<FleetDashboard />} />
            <Route path="fleet/map" element={<FleetMap />} />
            <Route path="fleet/visits" element={<FleetVisits />} />
            <Route path="fleet/clinicians" element={<FleetClinicians />} />
            <Route path="fleet/vehicles" element={<Protected adminOnly><FleetVehicles /></Protected>} />

            <Route path="teleconsult" element={<TeleconsultDashboard />} />
            <Route path="teleconsult/room/:id" element={<TeleconsultRoom />} />

            <Route path="clinical" element={<ClinicalDashboard mode="active" />} />
            <Route path="clinical/my-patients" element={<ClinicalDashboard mode="mine" />} />
            <Route path="clinical/high-risk" element={<ClinicalDashboard mode="high_risk" />} />
            <Route path="clinical/tasks" element={<ClinicalTasks />} />
            <Route path="clinical/patients/:id" element={<ClinicalPatientProfile />} />

            <Route path="command" element={<ExecutiveDashboard />} />
            <Route path="command/operational" element={<OperationalDashboard />} />
            <Route path="command/clinical-safety" element={<ClinicalSafetyDashboard />} />
            <Route path="command/population" element={<PopulationHealthDashboard />} />
            <Route path="command/interventions" element={<InterventionsDashboard />} />
            <Route path="command/sla" element={<SlaManager />} />
            <Route path="command/patient-explorer" element={<PatientExplorer />} />
            <Route path="command/reports" element={<Navigate to="/reports" replace />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
